import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeft, 
  Edit, 
  Check, 
  X, 
  Clock, 
  Calendar, 
  User, 
  FileText,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Download,
  Play,
  Pause,
  Square,
} from 'lucide-react';
import { timesheetAPI, revocationAPI } from '../services/api';
import { exportTimesheetPdf } from '../utils/timesheetPdf';
import { projectAPI } from '../services/api';
import toast from 'react-hot-toast';

const AdminTimesheetDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revocationReason, setRevocationReason] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timerStartTime, setTimerStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showDayDetails, setShowDayDetails] = useState(false);

  // Real-time clock update
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Timer functionality
  useEffect(() => {
    let interval = null;
    if (isTimerRunning) {
      interval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else if (!isTimerRunning && elapsedTime !== 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, elapsedTime]);

  const startTimer = () => {
    setIsTimerRunning(true);
    setTimerStartTime(new Date());
  };

  const pauseTimer = () => {
    setIsTimerRunning(false);
  };

  const stopTimer = () => {
    setIsTimerRunning(false);
    setElapsedTime(0);
    setTimerStartTime(null);
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
    setShowDayDetails(true);
  };

  const closeDayDetails = () => {
    setShowDayDetails(false);
    setSelectedDay(null);
  };

  const { data: timesheet, isLoading, error } = useQuery(
    ['admin-timesheet', id],
    () => timesheetAPI.getAdminTimesheetById(id),
    {
      retry: false, // Don't retry on error (especially for 404s)
      onError: (error) => {
        const statusCode = error.response?.status;
        const isNotFound = statusCode === 404 || statusCode === 400;
        const isServerError = statusCode === 500 || statusCode >= 500;
        
        if (isNotFound) {
          toast.error('Timesheet not found. It may have been deleted.');
        } else if (isServerError) {
          toast.error('Server error occurred. Please try again later.');
        } else {
          toast.error('Failed to load timesheet details.');
        }
      },
      select: (response) => {
        console.log('=== ADMIN TIMESHEET DETAILS DEBUG ===');
        console.log('Full API response:', response);
        console.log('Response status:', response.status);
        console.log('Response headers:', response.headers);
        
        const data = response.data;
        console.log('Raw timesheet data:', data);
        console.log('Data type:', typeof data);
        console.log('Data keys:', data ? Object.keys(data) : 'No data');
        
        if (data && data.data) {
          console.log('Nested data found:', data.data);
          console.log('Nested data employee:', data.data.employee);
          console.log('Nested data entries:', data.data.entries);
        }
        
        console.log('Employee data:', data?.employee);
        console.log('Employee data type:', typeof data?.employee);
        console.log('Employee data keys:', data?.employee ? Object.keys(data.employee) : 'No employee data');
        
        if (!data) {
          console.error('No data received from API');
          return null;
        }
        
        // Handle nested data structure
        const actualData = data.data || data;
        console.log('Using actual data:', actualData);
        
        // Ensure we have a valid timesheet object with all required fields
        const timesheetData = {
          id: actualData.id,
          employee: actualData.employee || null,
          startDate: actualData.startDate,
          endDate: actualData.endDate,
          totalHours: actualData.totalHours || 0,
          status: actualData.status,
          description: actualData.description || '',
          approvedBy: actualData.approvedBy || null,
          approvedAt: actualData.approvedAt || null,
          rejectionReason: actualData.rejectionReason || null,
          revocationReason: actualData.revocationReason || null,
          revokedBy: actualData.revokedBy || null,
          createdAt: actualData.createdAt || null,
          updatedAt: actualData.updatedAt || null,
          submittedAt: actualData.submittedAt || null,
          entries: []
        };
        
        console.log('Constructed timesheet data:', timesheetData);
        console.log('Employee in timesheet data:', timesheetData.employee);
        
        if (actualData.entries && Array.isArray(actualData.entries)) {
          console.log('Raw entries:', actualData.entries);
          
          // Map backend field names to frontend expected field names
          const mappedEntries = actualData.entries.map(entry => {
            console.log('Raw entry:', entry);
            console.log('Entry keys:', Object.keys(entry));
            console.log('Entry date field:', entry.date);
            console.log('Entry entryDate field:', entry.entryDate);
            console.log('Entry hours field:', entry.hours);
            console.log('Entry hoursWorked field:', entry.hoursWorked);
            console.log('Entry project field:', entry.project);
            console.log('Entry projectName field:', entry.projectName);
            console.log('Entry task field:', entry.task);
            console.log('Entry taskDescription field:', entry.taskDescription);
            return {
              id: entry.id,
              date: entry.date ? new Date(entry.date).toISOString().split('T')[0] : 
                    entry.entryDate ? new Date(entry.entryDate).toISOString().split('T')[0] : null,
              hours: entry.hours || entry.hoursWorked || 0,
              project: entry.project?.name || entry.projectName || entry.project || '',
              projectId: entry.project?.id || entry.projectId || null,
              description: entry.task || entry.taskDescription || 'No description provided',
              // Add any missing fields with defaults
              taskName: entry.taskName || '',
              notes: entry.notes || '',
              startTime: entry.startTime || '',
              endTime: entry.endTime || ''
            };
          });
          
          console.log('Mapped entries:', mappedEntries);
          timesheetData.entries = mappedEntries;
        }
        
        console.log('Final timesheet data:', timesheetData);
        console.log('=== END ADMIN TIMESHEET DETAILS DEBUG ===');
        return timesheetData;
      },
      enabled: !!id && !!user,
    }
  );

  const approveMutation = useMutation(
    () => timesheetAPI.approveTimesheet(id),
    {
      onSuccess: () => {
        toast.success('Timesheet approved successfully');
        queryClient.invalidateQueries(['admin-timesheet', id]);
        queryClient.invalidateQueries('admin-timesheets');
        setShowApprovalModal(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to approve timesheet');
      },
    }
  );

  const rejectMutation = useMutation(
    (reason) => timesheetAPI.rejectTimesheet(id, reason),
    {
      onSuccess: () => {
        toast.success('Timesheet rejected successfully');
        queryClient.invalidateQueries(['admin-timesheet', id]);
        queryClient.invalidateQueries('admin-timesheets');
        setShowApprovalModal(false);
        setRejectionReason('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to reject timesheet');
      },
    }
  );

  const revokeMutation = useMutation(
    (revocationReason) => {
      if (user?.role === 'SUPER_ADMIN') {
        return revocationAPI.revokeTimesheetSuperAdmin(id, revocationReason);
      } else {
        return revocationAPI.revokeTimesheet(id, revocationReason);
      }
    },
    {
      onSuccess: () => {
        toast.success('Timesheet revoked to draft successfully');
        queryClient.invalidateQueries(['admin-timesheet', id]);
        queryClient.invalidateQueries('admin-timesheets');
        setShowRevokeModal(false);
        setRevocationReason('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to revoke timesheet');
      },
    }
  );

  const handleRevoke = () => {
    if (!revocationReason.trim()) {
      toast.error('Please provide a revocation reason');
      return;
    }
    revokeMutation.mutate(revocationReason.trim());
  };

  // Fetch project managers for projects included in this timesheet (Admin/Super Admin only)
  const projectIds = React.useMemo(() => {
    const ids = (timesheet?.entries || [])
      .map(e => e.projectId)
      .filter(id => !!id);
    return Array.from(new Set(ids));
  }, [timesheet]);

  const { data: projectManagers = [], isLoading: managersLoading } = useQuery(
    ['admin-timesheet-project-managers', id, projectIds.join(',')],
    async () => {
      const results = await Promise.all(
        projectIds.map(pid => projectAPI.getProjectById(pid))
      );
      // Flatten unique managers across all projects
      const managersMap = new Map();
      results.forEach(res => {
        const project = res.data?.data || res.data;
        const managers = project?.assignedManagers || [];
        managers.forEach(m => {
          if (!managersMap.has(m.id)) managersMap.set(m.id, m);
        });
      });
      return Array.from(managersMap.values());
    },
    {
      enabled: (user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && projectIds.length > 0,
    }
  );

  const getStatusIcon = (status) => {
    switch (status) {
      case 'DRAFT':
        return <Clock className="w-5 h-5 text-gray-500" />;
      case 'SUBMITTED':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'PENDING':
        return <RefreshCw className="w-5 h-5 text-orange-500" />;
      case 'REUPDATED':
        return <RefreshCw className="w-5 h-5 text-blue-500" />;
      case 'APPROVED':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'REJECTED':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      case 'SUBMITTED':
        return 'bg-yellow-100 text-yellow-800';
      case 'PENDING':
        return 'bg-orange-100 text-orange-800';
      case 'REUPDATED':
        return 'bg-blue-100 text-blue-800';
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateTotalHours = (entries) => {
    return entries?.reduce((total, entry) => total + (entry.hours || 0), 0) || 0;
  };

  const handleApproval = (action) => {
    setApprovalAction(action);
    setShowApprovalModal(true);
  };

  const confirmApproval = () => {
    if (approvalAction === 'approve') {
      // Close modal immediately
      setShowApprovalModal(false);
      
      // Show instant success
      toast.success('Timesheet approved! Employee notified via email.');
      
      // Navigate back immediately
      setTimeout(() => {
        navigate('/admin/timesheets/review');
      }, 500);
      
      // Call API in background
      approveMutation.mutate();
    } else if (approvalAction === 'reject') {
      if (!rejectionReason.trim()) {
        toast.error('Please provide a reason for rejection');
        return;
      }
      
      // Close modal immediately
      setShowApprovalModal(false);
      
      // Show instant success
      toast.success('Timesheet rejected! Employee notified via email.');
      
      // Navigate back immediately
      setTimeout(() => {
        navigate('/admin/timesheets/review');
      }, 500);
      
      // Call API in background
      rejectMutation.mutate(rejectionReason);
    }
  };

  const handleExport = async () => {
    try {
      await exportTimesheetPdf(timesheet, { template: 'classic' });
    } catch (e) {
      console.error('Failed to export PDF', e);
      toast.error('Failed to export PDF');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    console.error('AdminTimesheetDetails - Error loading timesheet:', error);
    const statusCode = error.response?.status;
    const isNotFound = statusCode === 404 || statusCode === 400;
    const isServerError = statusCode === 500 || statusCode >= 500;
    const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to load timesheet details';
    const isDeleted = errorMessage.toLowerCase().includes('not found') || errorMessage.toLowerCase().includes('deleted');
    
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md">
          <AlertCircle className="mx-auto h-16 w-16 text-red-500 mb-4" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">
            {isNotFound || isDeleted ? 'Timesheet Not Found' : isServerError ? 'Server Error' : 'Error Loading Timesheet'}
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            {isDeleted || isNotFound 
              ? 'This timesheet may have been deleted or does not exist. Please navigate back to view other timesheets.'
              : isServerError
              ? 'An internal server error occurred while loading the timesheet. Please try again later or contact support if the problem persists.'
              : errorMessage}
          </p>
          <div className="mt-6 flex justify-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Go Back
            </button>
            <button
              onClick={() => {
                const currentUserRole = typeof user?.role === 'object' ? user?.role?.name : user?.role;
                const redirectUrl = currentUserRole === 'SUPER_ADMIN' 
                  ? '/super-admin/timesheets' 
                  : '/admin/timesheets/review';
                navigate(redirectUrl);
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            >
              View All Timesheets
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!timesheet) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Timesheet not found</h3>
          <p className="mt-1 text-sm text-gray-500">The requested timesheet could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 overflow-x-hidden pb-6">
      {/* Header - stacks on mobile */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm p-4 md:p-6"
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors shrink-0 touch-manipulation"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <FileText className="w-7 h-7 text-primary-600 shrink-0 md:w-8 md:h-8" />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-lg md:text-2xl font-bold text-gray-900">Admin Timesheet Details</h1>
                    <span className="px-2 py-0.5 text-xs md:text-sm font-medium bg-gray-100 text-gray-700 rounded-lg border border-gray-300">
                      ID: {timesheet.id}
                    </span>
                  </div>
                  <p className="text-gray-600 mt-0.5 text-sm truncate">
                    {timesheet.employee?.firstName || 'Unknown'} {timesheet.employee?.lastName || 'Employee'} · Week of {timesheet.startDate ? new Date(timesheet.startDate).toLocaleDateString() : '—'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4 md:border-0 md:pt-0 md:ml-auto">
            <button
              onClick={handleExport}
              className="flex-1 min-w-[120px] md:flex-none px-4 py-3 md:py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 touch-manipulation"
            >
              <Download className="w-4 h-4" />
              <span>Export PDF</span>
            </button>
            {timesheet.status !== 'APPROVED' && timesheet.status !== 'REJECTED' && timesheet.status !== 'PENDING' && (
              <>
                <button
                  onClick={() => handleApproval('reject')}
                  disabled={rejectMutation.isLoading}
                  className="flex-1 min-w-[100px] md:flex-none px-4 py-3 md:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 touch-manipulation"
                >
                  <X className="w-4 h-4" />
                  <span>Reject</span>
                </button>
                <button
                  onClick={() => handleApproval('approve')}
                  disabled={approveMutation.isLoading}
                  className="flex-1 min-w-[100px] md:flex-none px-4 py-3 md:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 touch-manipulation"
                >
                  <Check className="w-4 h-4" />
                  <span>Approve</span>
                </button>
              </>
            )}
            {timesheet.status === 'APPROVED' && (user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN') && (
              <button
                onClick={() => setShowRevokeModal(true)}
                disabled={revokeMutation.isLoading}
                className="w-full md:w-auto px-4 py-3 md:py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 touch-manipulation"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Revoke to Draft</span>
              </button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Timesheet Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg shadow-sm p-4 md:p-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">Employee Information</h3>
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Name:</span> {timesheet.employee?.firstName || 'Unknown'} {timesheet.employee?.lastName || 'Employee'}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Email:</span> {timesheet.employee?.email || 'Not available'}
              </p>
              {timesheet.approvedBy && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Approved by:</span> {`${timesheet.approvedBy.firstName || ''} ${timesheet.approvedBy.lastName || ''}`.trim() || 'N/A'}
                  {timesheet.approvedBy.role && (
                    <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 align-middle">
                      {typeof timesheet.approvedBy.role === 'object' ? (timesheet.approvedBy.role.name || 'MANAGER') : timesheet.approvedBy.role}
                    </span>
                  )}
                </p>
              )}
              {/* Project Managers across all projects in this timesheet */}
              {(managersLoading || projectManagers.length > 0 || timesheet.approvedBy) && (
                <div className="pt-1">
                  <p className="text-sm font-medium text-gray-900 mb-1">Project Managers</p>
                  {managersLoading ? (
                    <p className="text-sm text-gray-500">Loading managers...</p>
                  ) : projectManagers.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {projectManagers.map(m => (
                        <span key={m.id} className="inline-flex items-center space-x-2 px-2.5 py-1 rounded-md bg-blue-50 text-blue-800 border border-blue-200">
                          <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-500 text-white text-xs font-semibold">
                            {(m.firstName?.[0] || '')}{(m.lastName?.[0] || '')}
                          </span>
                          <span className="text-sm">
                            {m.firstName} {m.lastName}
                          </span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    // Fallback to approver badge only (no extra message)
                    <div className="flex items-center flex-wrap gap-2">
                      <span className="inline-flex items-center space-x-2 px-2.5 py-1 rounded-md bg-green-50 text-green-800 border border-green-200">
                        <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-green-500 text-white text-xs font-semibold">
                          {(timesheet.approvedBy.firstName?.[0] || '')}{(timesheet.approvedBy.lastName?.[0] || '')}
                        </span>
                        <span className="text-sm">
                          {timesheet.approvedBy.firstName} {timesheet.approvedBy.lastName}
                        </span>
                        <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-800">
                          Approver{(typeof timesheet.approvedBy.role === 'object' ? timesheet.approvedBy.role?.name : timesheet.approvedBy.role) === 'MANAGER' ? ' (Manager)' : ''}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">Timesheet Period</h3>
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Start Date:</span> {timesheet.startDate ? new Date(timesheet.startDate).toLocaleDateString() : 'Invalid Date'}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">End Date:</span> {timesheet.endDate ? new Date(timesheet.endDate).toLocaleDateString() : 'Invalid Date'}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Total Hours:</span> {calculateTotalHours(timesheet.entries)}
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">Status Information</h3>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                {getStatusIcon(timesheet.status)}
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(timesheet.status)}`}>
                  {timesheet.status}
                </span>
              </div>
              {timesheet.submittedAt && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Submitted:</span> {new Date(timesheet.submittedAt).toLocaleDateString()} at {new Date(timesheet.submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
              <p className="text-sm text-gray-600">
                <span className="font-medium">Created:</span> {timesheet.createdAt ? new Date(timesheet.createdAt).toLocaleDateString() : 'Unknown'} {timesheet.createdAt && `at ${new Date(timesheet.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Updated:</span> {timesheet.updatedAt ? new Date(timesheet.updatedAt).toLocaleDateString() : 'Unknown'} {timesheet.updatedAt && `at ${new Date(timesheet.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
              </p>
              {timesheet.approvedAt && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Approved:</span> {new Date(timesheet.approvedAt).toLocaleDateString()} at {new Date(timesheet.approvedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
              {timesheet.approvedBy && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Approved by:</span> {`${timesheet.approvedBy.firstName || ''} ${timesheet.approvedBy.lastName || ''}`.trim() || 'N/A'}
                  {timesheet.approvedBy.role && (
                    <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 align-middle">
                      {typeof timesheet.approvedBy.role === 'object' ? (timesheet.approvedBy.role.name || 'MANAGER') : timesheet.approvedBy.role}
                    </span>
                  )}
                </p>
              )}
              {timesheet.rejectionReason && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm text-red-800">
                    <span className="font-medium">Rejection Reason:</span> {timesheet.rejectionReason}
                  </p>
                </div>
              )}
              {timesheet.revocationReason && (
                <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded">
                  <p className="text-sm text-orange-800">
                    <span className="font-medium">Revocation Reason:</span> {timesheet.revocationReason}
                  </p>
                  {timesheet.revokedBy && (
                    <p className="text-xs text-orange-700 mt-1">
                      Revoked by: {timesheet.revokedBy?.firstName || ''} {timesheet.revokedBy?.lastName || ''}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">Summary</h3>
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Entries:</span> {timesheet.entries?.length || 0}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Projects:</span> {new Set(timesheet.entries?.map(e => e.project).filter(Boolean)).size || 0}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Avg Hours/Day:</span> {(calculateTotalHours(timesheet.entries) / 7).toFixed(1)}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Interactive Calendar View - scrollable on mobile */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg shadow-sm border overflow-hidden"
      >
        {/* Calendar Header */}
        <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h2 className="text-base md:text-lg font-semibold text-gray-900">Timesheet Calendar</h2>
              <p className="text-xs md:text-sm text-gray-600">Tap any day to view entries</p>
            </div>
            <div className="text-left sm:text-right">
              <span className="text-xs md:text-sm text-gray-500">Total </span>
              <span className="text-lg md:text-xl font-bold text-blue-600">
                {calculateTotalHours(timesheet.entries).toFixed(1)}h
              </span>
            </div>
          </div>
        </div>
        
        {/* Calendar Grid - compact on mobile like employee view */}
        <div className="p-3 md:p-6">
          {(() => {
            const startDate = new Date(timesheet.startDate);
            const endDate = new Date(timesheet.endDate);
            
            const calendarDays = [];
            const currentDate = new Date(startDate);
            const startDayOfWeek = startDate.getDay();
            for (let i = 0; i < startDayOfWeek; i++) {
              const prevDate = new Date(startDate);
              prevDate.setDate(startDate.getDate() - (startDayOfWeek - i));
              calendarDays.push({ date: prevDate, isCurrentWeek: false });
            }
            while (currentDate <= endDate) {
              calendarDays.push({ date: new Date(currentDate), isCurrentWeek: true });
              currentDate.setDate(currentDate.getDate() + 1);
            }
            const endDayOfWeek = endDate.getDay();
            for (let i = endDayOfWeek + 1; i < 7; i++) {
              const nextDate = new Date(endDate);
              nextDate.setDate(endDate.getDate() + (i - endDayOfWeek));
              calendarDays.push({ date: nextDate, isCurrentWeek: false });
            }

            return (
              <div className="space-y-2 md:space-y-4">
                {/* Day headers: S M T W T F S (like employee / reference) */}
                <div className="grid grid-cols-7 gap-1">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
                    <div key={`day-${idx}`} className="text-center text-xs font-bold text-gray-500 py-1 md:p-3 md:bg-gray-50 md:rounded-lg">
                      {day}
                    </div>
                  ))}
                </div>
                {/* Calendar days - same style as employee: date + blue circle with count */}
                <div className="grid grid-cols-7 gap-1.5 md:gap-1">
                  {calendarDays.map((dayData, index) => {
                    const day = dayData.date;
                    const dayEntries = timesheet.entries?.filter(entry => 
                      entry.date && new Date(entry.date).toDateString() === day.toDateString()
                    ) || [];
                    const dayTotal = dayEntries.reduce((sum, entry) => sum + (entry.hours || 0), 0);
                    const isToday = day.toDateString() === new Date().toDateString();
                    const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                    const hasEntries = dayEntries.length > 0;
                    
                    return (
                      <motion.div
                        key={index}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => dayData.isCurrentWeek && handleDayClick(day)}
                        className={`min-h-[80px] md:min-h-[140px] p-2 md:p-3 border-2 md:border rounded-xl md:rounded-lg cursor-pointer touch-manipulation flex flex-col ${
                          dayData.isCurrentWeek 
                            ? hasEntries
                              ? 'bg-blue-50 border-blue-400 md:border-blue-200 active:bg-blue-100'
                              : 'bg-white border-gray-300 md:border-gray-200 active:bg-gray-50'
                            : 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-50'
                        } ${isToday ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
                      >
                        <div className="flex items-center justify-center mb-1">
                          <span className={`text-sm font-bold ${
                            dayData.isCurrentWeek 
                              ? isToday ? 'text-blue-600' : isWeekend ? 'text-gray-500' : 'text-gray-900'
                              : 'text-gray-400'
                          }`}>
                            {day.getDate()}
                          </span>
                        </div>
                        {/* Blue circle with entry count (mobile + desktop) */}
                        {dayData.isCurrentWeek && dayEntries.length > 0 && (
                          <div className="mt-auto flex justify-center">
                            <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                              {dayEntries.length}
                            </div>
                          </div>
                        )}
                        {/* Desktop only: hours badge + project preview */}
                        {dayData.isCurrentWeek && dayTotal > 0 && (
                          <span className="hidden md:inline-block text-xs font-medium text-green-600 bg-green-100 px-2 py-0.5 rounded-full mt-1">
                            {dayTotal.toFixed(1)}h
                          </span>
                        )}
                        {dayData.isCurrentWeek && dayEntries.length > 0 && (
                          <div className="hidden md:block space-y-0.5 mt-1">
                            {dayEntries.slice(0, 2).map((entry, entryIndex) => (
                              <div
                                key={entryIndex}
                                className="text-xs p-2 bg-white border border-blue-200 rounded truncate"
                                title={`${entry.project || 'No Project'}: ${entry.hours?.toFixed(1) ?? 0}h`}
                              >
                                <div className="font-medium text-blue-800 truncate">{entry.project || 'No Project'}</div>
                                <div className="text-blue-600">{(entry.hours ?? 0).toFixed(1)}h</div>
                              </div>
                            ))}
                            {dayEntries.length > 2 && (
                              <div className="text-xs text-gray-500 text-center bg-gray-100 rounded p-1">
                                +{dayEntries.length - 2} more
                              </div>
                            )}
                          </div>
                        )}
                        {dayData.isCurrentWeek && dayEntries.length === 0 && (
                          <div className="mt-auto text-xs text-gray-400 text-center hidden md:block">
                            {isWeekend ? 'Weekend' : 'No entries'}
                          </div>
                        )}
                        {dayData.isCurrentWeek && dayEntries.length === 0 && !isWeekend && (
                          <div className="mt-auto text-xs text-gray-400 text-center">-</div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Summary Stats */}
        <div className="border-t border-gray-200 px-4 md:px-6 py-3 md:py-4 bg-gray-50">
          <div className="grid grid-cols-3 gap-2 md:gap-4">
            <div className="text-center py-1">
              <div className="text-lg md:text-2xl font-bold text-blue-600">{timesheet.entries?.length || 0}</div>
              <div className="text-xs md:text-sm text-gray-600">Entries</div>
            </div>
            <div className="text-center py-1">
              <div className="text-lg md:text-2xl font-bold text-green-600">{calculateTotalHours(timesheet.entries).toFixed(1)}h</div>
              <div className="text-xs md:text-sm text-gray-600">Hours</div>
            </div>
            <div className="text-center py-1">
              <div className="text-lg md:text-2xl font-bold text-purple-600">{new Set(timesheet.entries?.map(e => e.project).filter(Boolean)).size || 0}</div>
              <div className="text-xs md:text-sm text-gray-600">Projects</div>
            </div>
          </div>
        </div>

        {timesheet.entries?.length === 0 && (
          <div className="px-6 py-12 text-center">
            <Clock className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No time entries</h3>
            <p className="mt-1 text-sm text-gray-500">This timesheet has no time entries recorded.</p>
          </div>
        )}
      </motion.div>

      {/* Day Details Modal - full screen on mobile */}
      {showDayDetails && selectedDay && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center p-0 md:p-4 z-50">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-t-2xl md:rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] md:max-h-[80vh] overflow-hidden"
          >
            {/* Modal Header */}
            <div className="px-4 md:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h3 className="text-base md:text-lg font-semibold text-gray-900">
                  {selectedDay.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </h3>
                <p className="text-sm text-gray-600 mt-0.5">
                  {(() => {
                    const dayEntries = timesheet.entries?.filter(entry => 
                      entry.date && new Date(entry.date).toDateString() === selectedDay.toDateString()
                    ) || [];
                    const dayTotal = dayEntries.reduce((sum, entry) => sum + (entry.hours || 0), 0);
                    return `${dayEntries.length} entries · ${dayTotal.toFixed(1)}h`;
                  })()}
                </p>
              </div>
              <button
                onClick={closeDayDetails}
                className="p-2 -mt-1 text-gray-400 hover:text-gray-600 transition-colors shrink-0 touch-manipulation"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-4 md:p-6 overflow-y-auto max-h-[60vh] md:max-h-[60vh]">
              {(() => {
                const dayEntries = timesheet.entries?.filter(entry => 
                  entry.date && new Date(entry.date).toDateString() === selectedDay.toDateString()
                ) || [];
                
                if (dayEntries.length === 0) {
                  return (
                    <div className="text-center py-8">
                      <Clock className="mx-auto h-12 w-12 text-gray-300" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No entries for this day</h3>
                      <p className="mt-1 text-sm text-gray-500">No time entries were recorded on this day.</p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-4">
                    {dayEntries.map((entry, entryIndex) => (
                      <motion.div
                        key={entryIndex}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: entryIndex * 0.1 }}
                        className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <h4 className="font-medium text-gray-900 text-lg">
                            {entry.project || 'No Project'}
                          </h4>
                          <span className="text-lg font-bold text-green-600 flex-shrink-0 ml-2">
                            {entry.hours.toFixed(1)}h
                          </span>
                        </div>
                        
                        <div className="text-sm text-gray-700 mb-3">
                          {(() => {
                            const description = entry.description || 'No description provided';
                            // Check if description contains bullet points (lines starting with • or -)
                            const lines = description.split('\n').filter(line => line.trim());
                            const hasBullets = lines.some(line => line.trim().match(/^[•\-\*]/));
                            
                            if (hasBullets) {
                              // Render as bullet list
                              return (
                                <ul className="list-none space-y-1 ml-2">
                                  {lines.map((line, idx) => {
                                    const cleanLine = line.trim().replace(/^[•\-\*]\s*/, '');
                                    return cleanLine ? (
                                      <li key={idx} className="flex items-start">
                                        <span className="text-primary-600 mr-2 flex-shrink-0">•</span>
                                        <span className="break-words flex-1">{cleanLine}</span>
                                      </li>
                                    ) : null;
                                  })}
                                </ul>
                              );
                            } else {
                              // Render as paragraph
                              return (
                                <p className="break-words whitespace-pre-wrap overflow-wrap-anywhere">
                                  {description}
                                </p>
                              );
                            }
                          })()}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          {entry.taskName && (
                            <div>
                              <span className="font-medium text-gray-600">Task:</span>
                              <span className="ml-2 text-gray-900">{entry.taskName}</span>
                            </div>
                          )}
                          {(entry.startTime || entry.endTime) && (
                            <div>
                              <span className="font-medium text-gray-600">Time:</span>
                              <span className="ml-2 text-gray-900">
                                {entry.startTime && entry.endTime 
                                  ? `${entry.startTime} - ${entry.endTime}`
                                  : entry.startTime || entry.endTime
                                }
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {entry.notes && (
                          <div className="mt-3 p-3 bg-yellow-50 border-l-4 border-yellow-300 rounded">
                            <p className="text-sm text-yellow-800">
                              <strong>Note:</strong> {entry.notes}
                            </p>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                );
              })()}
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end">
                <button
                  onClick={closeDayDetails}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Revoke Modal */}
      {showRevokeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full"
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Revoke Timesheet to Draft
              </h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to revoke this timesheet back to draft status? The employee will be notified and can make changes before resubmitting.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Revocation Reason *
                </label>
                <textarea
                  value={revocationReason}
                  onChange={(e) => setRevocationReason(e.target.value)}
                  placeholder="Please provide a detailed reason for revoking this timesheet..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                  rows={4}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  This reason will be visible to the employee, manager, and all admins when they view this timesheet.
                </p>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowRevokeModal(false);
                    setRevocationReason('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRevoke}
                  disabled={revokeMutation.isLoading}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {revokeMutation.isLoading && (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  )}
                  <span>Revoke to Draft</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full"
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {approvalAction === 'approve' ? 'Approve Timesheet' : 'Reject Timesheet'}
              </h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                {approvalAction === 'approve' 
                  ? 'Are you sure you want to approve this timesheet? This action cannot be undone.'
                  : 'Are you sure you want to reject this timesheet? The employee will be notified and can resubmit.'
                }
              </p>
              
              {approvalAction === 'reject' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Rejection *
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Please provide a detailed reason for rejecting this timesheet..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                    rows={3}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This reason will be visible to the employee when they view their timesheet.
                  </p>
                </div>
              )}
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowApprovalModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmApproval}
                  disabled={approveMutation.isLoading || rejectMutation.isLoading}
                  className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2 ${
                    approvalAction === 'approve' 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {(approveMutation.isLoading || rejectMutation.isLoading) && (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  )}
                  <span>
                    {approvalAction === 'approve' ? 'Approve' : 'Reject'}
                  </span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminTimesheetDetails;